import React, { useState } from 'react'
import Modal from 'blocks.simple/modal/modal'
import Icon from 'blocks.simple/icon/icon'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import { Button } from '../../../molecules/Button'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import { cn } from 'ethcss'
import styles from './filter.jcss'
import { text as textStyles, item, grid } from 'theme'
import translate from 'core/translate'
import EditText from 'blocks.simple/editText/editText'
import { INotificationFilterFields, IState } from '../NotificationСenterHooks'
import { FilterButton } from 'molecules/FilterButton'
import { Typography } from 'atoms/Typography'
import { changeFilter } from 'features/routes'
import helpers from 'core/helpers'

interface Props {
    filterFields: INotificationFilterFields
    setState: (state: any) => void
    state: IState
}

const NotificationFilter: React.FC<Props> = ({ filterFields, setState, state }) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [selectedFilter, setSelectedFilter] = useState<any>(null)
    const [selected, setSelected] = useState<Pick<INotificationFilterFields, 'status' | 'type' | 'category'>>({
        status: [],
        type: [],
        category: [],
    })
    const [isDateSelected, setIsDateSelected] = useState(false)

    const notificationOptions = [
        { id: 'status', name: translate('status') },
        { id: 'type', name: translate('type') },
        { id: 'category', name: translate('category') },
        { id: 'date', name: translate('date') },
    ]

    const openFilter = () => {
        const appliedFilter = notificationOptions.find(({ id }) => {
            if (id === 'date') return !!(filterFields.startDate || filterFields.endDate)

            return filterFields[id as 'status' | 'type' | 'category'].length > 0
        })

        setIsFilterOpen(true)
        setSelectedFilter(appliedFilter || null)
        setSelected({
            status: [...filterFields.status],
            type: [...filterFields.type],
            category: [...filterFields.category],
        })
        setIsDateSelected(!!(filterFields.startDate || filterFields.endDate))
        setState((prevState: IState) => ({
            ...prevState,
            startDate: filterFields.startDate || prevState.startDate,
            endDate: filterFields.endDate || prevState.endDate,
        }))
    }

    const closeFilter = () => setIsFilterOpen(false)

    const onFilterChange = () => {
        const prefix = (field: string) => helpers.prefixQuery({ name: 'notificationCenter', field })

        changeFilter(
            {
                [prefix('status')]: selected.status.join('//'),
                [prefix('type')]: selected.type.join('//'),
                [prefix('category')]: selected.category.join('//'),
                [prefix('startDate')]: isDateSelected ? state.startDate : '',
                [prefix('endDate')]: isDateSelected ? state.endDate : '',
            },
            false
        )
        closeFilter()
    }

    const resetFilter = () => {
        setSelected({ status: [], type: [], category: [] })
        setIsDateSelected(false)
        setSelectedFilter(null)
    }

    const selectFilter = (filter: any) => {
        setSelectedFilter(filter)
        if (filter.id === 'date') setIsDateSelected(true)
    }

    const toggleOption = (field: 'status' | 'type' | 'category', value: string) => {
        setSelected((current) => ({
            ...current,
            [field]: current[field].includes(value)
                ? current[field].filter((item) => item !== value)
                : [...current[field], value],
        }))
    }

    const hasAppliedFilters = !!(
        filterFields.status.length ||
        filterFields.type.length ||
        filterFields.category.length ||
        filterFields.startDate ||
        filterFields.endDate
    )
    return (
        <div className={styles.filter}>
            <FilterButton active={isFilterOpen || hasAppliedFilters} onClick={openFilter} />
            <div className={item.abs}>
                <Modal open={isFilterOpen} showCloseIcon={false} mod="clear" transitionName="fade">
                    <div className={styles.modalWrapper}>
                        <section
                            className={styles.modalInner}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="notification-filter-title"
                        >
                            <header className={styles.modalHeader}>
                                <div id="notification-filter-title">
                                    <Typography type="title">{translate('filter')}</Typography>
                                </div>
                                <button
                                    type="button"
                                    className={styles.closeButton}
                                    aria-label={translate('close')}
                                    onClick={closeFilter}
                                >
                                    <Icon type="close_tiny" size={16} />
                                </button>
                            </header>

                            <div className={styles.modalBody}>
                                <div className={cn(textStyles.mini, grid.col, styles.mobileScroll)}>
                                    <div className={styles.selectorField}>
                                        <Dropdown
                                            placeholder={translate('filter')}
                                            options={notificationOptions}
                                            onChange={selectFilter}
                                            value={selectedFilter ? selectedFilter.id : null}
                                        />
                                        {!selectedFilter && (
                                            <Typography type="text" className={cn(grid.pt_micro, textStyles.center)}>
                                                {translate('notificationsFilter')}
                                            </Typography>
                                        )}
                                    </div>

                                    {selectedFilter?.id === 'status' && (
                                        <div className={cn(grid.col, grid.space, styles.mobileScroll)}>
                                            <div className={styles.filterFields}>
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('status', 'read')}
                                                    checked={selected.status.includes('read')}
                                                    label={translate('read')}
                                                />
                                                <Checkbox
                                                    onClick={() => toggleOption('status', 'unread')}
                                                    checked={selected.status.includes('unread')}
                                                    label={translate('notRead')}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedFilter?.id === 'type' && (
                                        <div className={cn(grid.col, grid.space, styles.mobileScroll)}>
                                            <div className={styles.filterFields}>
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('type', 'information')}
                                                    checked={selected.type.includes('information')}
                                                    label={translate('information')}
                                                />
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('type', 'warning')}
                                                    checked={selected.type.includes('warning')}
                                                    label={translate('warning')}
                                                />
                                                <Checkbox
                                                    onClick={() => toggleOption('type', 'error')}
                                                    checked={selected.type.includes('error')}
                                                    label={translate('error')}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedFilter?.id === 'category' && (
                                        <div className={cn(grid.col, grid.space, styles.mobileScroll)}>
                                            <div className={styles.filterFields}>
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('category', 'default')}
                                                    checked={selected.category.includes('default')}
                                                    label={translate('information')}
                                                />
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('category', 'license')}
                                                    checked={selected.category.includes('license')}
                                                    label={translate('licensing')}
                                                />
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('category', 'digitalSignage')}
                                                    checked={selected.category.includes('digitalSignage')}
                                                    label={translate('digitalSignage_permissions')}
                                                />
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('category', 'engineeringWorks')}
                                                    checked={selected.category.includes('engineeringWorks')}
                                                    label={translate('engineeringWorks')}
                                                />
                                                <Checkbox
                                                    className={grid.pb_mini}
                                                    onClick={() => toggleOption('category', 'broadcast')}
                                                    checked={selected.category.includes('broadcast')}
                                                    label={translate('broadcasts')}
                                                />
                                                <Checkbox
                                                    onClick={() => toggleOption('category', 'content')}
                                                    checked={selected.category.includes('content')}
                                                    label={translate('content')}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {selectedFilter?.id === 'date' && (
                                        <div className={cn(grid.col, grid.space, grid.p_mini)}>
                                            <EditText
                                                containerClass={cn(grid.mt_md, grid.mb_mini)}
                                                type="date"
                                                label={translate('startDate')}
                                                dateValue={state.startDate}
                                                onChange={(startDate: string) => {
                                                    setIsDateSelected(true)
                                                    setState((prevState: IState) => ({ ...prevState, startDate }))
                                                }}
                                            />
                                            <EditText
                                                containerClass={styles.zIndex}
                                                type="date"
                                                label={translate('endDate')}
                                                dateValue={state.endDate}
                                                onChange={(endDate: string) => {
                                                    setIsDateSelected(true)
                                                    setState((prevState: IState) => ({ ...prevState, endDate }))
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <button type="button" className={styles.resetButton} onClick={resetFilter}>
                                    {translate('resetFilter')}
                                </button>
                            </div>
                            <footer className={styles.buttons}>
                                <Button className={cn(styles.actionButton)} onClick={closeFilter}>
                                    {translate('close')}
                                </Button>
                                <Button mod="fillBrand" className={cn(styles.actionButton)} onClick={onFilterChange}>
                                    {translate('apply')}
                                </Button>
                            </footer>
                        </section>
                    </div>
                </Modal>
            </div>
        </div>
    )
}

export default NotificationFilter
